import {
  createTheme,
  LinearProgressProps,
  Theme,
} from '@ltvco/refresh-lib/theme';

export const theme = createTheme({
  typography: {
    button: { textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' },
    fontSize: 16,
    htmlFontSize: 16,
    fontFamily: "'Nunito Sans', sans-serif",
    h1: {
      fontSize: '1.875rem',
      fontWeight: '700',
      margin: '0 0 12px 0',
      letterSpacing: '1.5px',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: '700',
      margin: '0 0 12px 0',
      letterSpacing: '-0.5px',
      lineHeight: 1.2,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: '700',
      margin: '0 0 12px 0',
      lineHeight: 1.2,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: '700',
      margin: '0 0 12px 0',
      letterSpacing: '0.25px',
      lineHeight: 1.2,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: '700',
      margin: '0 0 12px 0',
      lineHeight: 1.2,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: '700',
      margin: '0 0 12px 0',
      letterSpacing: '0.15px',
      lineHeight: 1.2,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: '400',
      letterSpacing: '0.15px',
      lineHeight: 1.2,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: '500',
      letterSpacing: '0.1px',
      lineHeight: 1.2,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: '400',
      letterSpacing: '0.15px',
      lineHeight: 1.2,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: '400',
      letterSpacing: '0.17px',
      lineHeight: 1.2,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: '400',
      letterSpacing: '0.4px',
      lineHeight: 1.2,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: '400',
      letterSpacing: '1px',
      lineHeight: 1.2,
    },
  },
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          backgroundColor: '#fff',
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: '#fff',
          },
          '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
            backgroundColor: '#F8F8F8',
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.checked && {
            '& .MuiSwitch-thumb': {
              backgroundColor: '#8736C1',
            },
            '&.report-preferences-switch': {
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#8736C1',
              },
            },
          }),
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: 32,
          boxShadow: 'none',
          ...(ownerState.variant === 'contained' &&
            ownerState.color === 'info' && {
              '&:hover': {
                color: '#ffffff',
              },
            }),

          '&.overview-monitor-vehicle': {
            backgroundColor: '#8736C1',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#8736C1',
            },

            '&.monitored': {
              backgroundColor: 'white',
              color: '#8736C1',
              border: '1px solid #8736C1',
            },
          },

          '&.back-to-top-button': {
            backgroundColor: '#F3EBF9',
            '&:hover': {
              backgroundColor: '#8736C1',
            },
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E2E2E2',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          input: {
            padding: 12,
          },
          div: {
            height: 40,
            backgroundColor: '#fff',
          },
          '& .MuiFormLabel-root': {
            top: -8,
          },
          '& .Mui-focused': {
            top: 0,
          },
          '& .MuiFormLabel-filled': {
            top: 0,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'success' && {
            '&.Mui-checked': {
              color: '#f1401d',
            },
          }),
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: (props: { ownerState: LinearProgressProps; theme: Theme }) => ({
          backgroundColor: (() => {
            switch (props.ownerState.color) {
              case 'primary':
                return '#8736C180';
              case 'success':
                return '';
              case 'error':
                return '#D32F2F80';
              default:
                return props?.theme?.palette?.progress?.main;
            }
          })(),
        }),
        bar: (props: { ownerState: LinearProgressProps; theme: Theme }) => ({
          backgroundColor: (() => {
            switch (props.ownerState.color) {
              case 'primary':
                return '#8736C1';
              case 'success':
                return '#388E3C';
              case 'error':
                return '#D32F2F';
              default:
                return props?.theme?.palette?.progress?.main;
            }
          })(),
        }),
      },
    },
    MuiStack: {
      styleOverrides: {
        root: () => ({
          '&.smart-insights-badge': {
            backgroundImage: `linear-gradient(to right, rgba(135,54,193,1) 0%, rgba(255, 71, 34, 1))`,
          },
          '&.market-analysis-container': {
            backgroundColor: '#F3EBF9',
          },
          '&.footer-privacy-secured': {
            backgroundColor: '#F3EBF9',
          },
        }),
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#E6E7E9 !important',
          '&.dark-mode': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '&.vehicle-detailed-history-view-link': {
            pointerEvents: 'none',
            color: '#8736C1',
          },
        },
      },
    },
  },
  palette: {
    button: {
      main: '#212121',
      light: '#525252',
      dark: '#0f0f0f',
      contrastText: '#FFFFFF',
    },
    buttonInverse: {
      main: '#FFFFFF',
      contrastText: '#0A0B0D',
      light: '#0A0B0D',
      dark: '#FFFFFF',
    },
    rats: {
      main: '#5B616E',
      light: '#F8F8F8',
      dark: '#8736C1',
      contrastText: '#FFFFFF',
      activeHover: '#512074',
      disabled: '#929AAB',
    },
    primary: {
      main: '#8736C1',
      dark: '#512074',
      light: '#F3EBF9',
      contrastText: '#fff',
    },
    info: {
      main: '#2196F3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#4caf50',
      dark: '#212121',
      light: '#81c784',
      contrastText: '#ffffff',
    },
    error: {
      main: '#DC0015',
      light: '#FFEDEA',
      dark: '#9A000E',
      contrastText: '#fff',
    },
    warning: {
      main: '#ED6C02',
      dark: '#E65100',
      light: '#FF9800',
      contrastText: '#000',
    },
    background: { paper: '#fff', default: '#f8f8f8', light: '#EEF0F3' },
    barChart: {
      main: '#8736C1',
      light: '#B783DC',
      dark: '#D3D3D7',
      contrastText: '#000',
    },
    lineChart: {
      main: '#8736C1',
      light: '#757575',
      dark: '#DC0015',
      contrastText: '#000',
    },
    progress: {
      main: '#8736C1',
      light: '#757575',
      dark: '#DC0015',
      contrastText: '#E2E2E2',
    },
    premium: { main: '#8736C1', contrastText: '#FFF' },
    listingPins: {
      main: '#8736C1',
      light: '#4AA351',
      dark: '#512074',
      contrastText: '#81b44c',
    },
    listingPinsVisited: {
      main: '#8736C1',
      light: '#9FCBA2',
      dark: '#9F97C1',
    },
    reportSectionSupertitleBackgroundColor: {
      main: '#512074',
    },
    text: {
      primary: '#0A0B0D',
      secondary: '#5B616E',
      disabled: '#929AAB',
      contrastText: '#fff',
    },
    labelsAndLinks: {
      main: '#757575',
    },
  },
});
